import { config } from "@/lib/config";
import { STORAGE_KEYS } from "@/lib/constants";

const API_BASE_URL = config.apiUrl;

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  skipAuth?: boolean;
}

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: unknown,
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = "ApiError";
  }
}

function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;

  // Try to read from Zustand persisted store first
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH_STORE);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.accessToken) {
        return parsed.state.accessToken;
      }
    }
  } catch {
    // Fall through to legacy key
  }

  // Fallback to legacy token key
  return localStorage.getItem(STORAGE_KEYS.LEGACY_TOKEN);
}

function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH_STORE);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.refreshToken) {
        return parsed.state.refreshToken;
      }
    }
  } catch {
    // No refresh token available
  }

  return null;
}

function setAuthTokens(accessToken: string, refreshToken: string): void {
  if (typeof window === "undefined") return;

  // Update the Zustand persisted store
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH_STORE);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state) {
        parsed.state.accessToken = accessToken;
        parsed.state.refreshToken = refreshToken;
        localStorage.setItem(STORAGE_KEYS.AUTH_STORE, JSON.stringify(parsed));
      }
    }
  } catch {
    // Ignore errors
  }

  // Also update the legacy key
  localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, accessToken);

  // Sync cookie for middleware route protection
  if (typeof document !== "undefined") {
    document.cookie = `access_token=${encodeURIComponent(accessToken)}; path=/; SameSite=Lax`;
  }
}

function clearAuth(): void {
  if (typeof window === "undefined") return;

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH_STORE);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state) {
        parsed.state.accessToken = null;
        parsed.state.refreshToken = null;
        parsed.state.user = null;
        parsed.state.isAuthenticated = false;
        localStorage.setItem(STORAGE_KEYS.AUTH_STORE, JSON.stringify(parsed));
      }
    }
  } catch {
    // Ignore errors
  }

  localStorage.removeItem(STORAGE_KEYS.LEGACY_TOKEN);

  // Clear the access_token cookie so middleware redirects to login
  if (typeof document !== "undefined") {
    document.cookie = "access_token=; path=/; SameSite=Lax; max-age=0";
  }
}

function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.append(key, value);
      }
    });
  }
  return url.toString();
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }

    try {
      const response = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        isRefreshing = false;
        refreshPromise = null;
        return false;
      }

      const data = await response.json();
      setAuthTokens(data.access_token, data.refresh_token);
      isRefreshing = false;
      refreshPromise = null;
      return true;
    } catch {
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }
  })();

  return refreshPromise;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, params, skipAuth = false } = options;

  const token = skipAuth ? null : getAuthToken();
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (token) {
    requestHeaders["Authorization"] = `Bearer ${token}`;
  }

  let response = await fetch(buildUrl(path, params), {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  // On 401, attempt token refresh and retry the request once
  if (response.status === 401 && !skipAuth) {
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      const newToken = getAuthToken();
      if (newToken) {
        requestHeaders["Authorization"] = `Bearer ${newToken}`;
      }

      response = await fetch(buildUrl(path, params), {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      });
    } else {
      // Refresh failed, clear auth and redirect to login
      clearAuth();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      throw new ApiError(401, "Unauthorized", null);
    }
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  get<T>(path: string, params?: Record<string, string>): Promise<T> {
    return request<T>(path, { method: "GET", params });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: "POST", body });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: "PUT", body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: "PATCH", body });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};

export { ApiError };
